const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

const buildHeaders = (roomId, isJson = true) => {
    const headers = {};
    if (isJson) headers['Content-Type'] = 'application/json';
    if (roomId) {
        headers['X-Room-Id'] = roomId;
    }
    return headers;
};

const appendRoomId = (url, roomId) => {
    if (!roomId) return url;
    return url.includes('?') ? `${url}&roomId=${encodeURIComponent(roomId)}` : `${url}?roomId=${encodeURIComponent(roomId)}`;
};

const SubmitGame = async (dataToSend, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/submit`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true),
            body: JSON.stringify(dataToSend)
        });

        if (response.status === 409) {
            return { success: false, status: 409, message: 'Duplicate entry' };
        }

        if (!response.ok) {
            return { success: false, status: response.status, message: 'Server error' };
        }

        const data = await response.json();
        return { success: true, data: data };
    } catch (error) {
        return { success: false, status: 0, message: 'Network error' };
    }
};

const ValidatePlayer = async (email, phone, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/check`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true),
            body: JSON.stringify({ email: email, phoneNumber: phone })
        });

        if (response.status === 409) {
            return { success: false, status: 409, message: 'Ya jugado' };
        }
        
        if (!response.ok) {
            return { success: false, status: response.status, message: 'Error server' };
        }

        return { success: true };
    } catch (error) {
        return { success: false, status: 0, message: 'Error red' };
    }
};

const GetSettings = async (roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/settings`, roomId);
        const response = await fetch(url, {
            method: 'GET',
            headers: buildHeaders(roomId, true)
        });
        if (!response.ok) {
            // Fallback por defecto seguro si el endpoint no existe en V2
            return {
                success: true,
                data: {
                    offsetY: 0,
                    bannerMode: 'invitation',
                    winnerName: '',
                    isOverlayVisible: false,
                    isGameActive: true
                }
            };
        }
        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return {
            success: true,
            data: {
                offsetY: 0,
                bannerMode: 'invitation',
                winnerName: '',
                isOverlayVisible: false,
                isGameActive: true
            }
        };
    }
};

const GetAdminParticipants = async (roomId = null, search = '', page = 1, pageSize = 50) => {
    try {
        let path = `${API_URL}PluckingTheRooster/admin/participants?page=${page}&pageSize=${pageSize}`;
        if (search && search.trim() !== '') {
            path += `&search=${encodeURIComponent(search.trim())}`;
        }
        const url = appendRoomId(path, roomId);

        const response = await fetch(url, {
            method: 'GET',
            headers: buildHeaders(roomId, true)
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al obtener participantes.' };
        }

        const data = await response.json();
        const items = Array.isArray(data) ? data : (data.items || data.data || []);
        const total = typeof data.total === 'number' ? data.total : items.length;

        return { success: true, data: items, total: total, raw: data };
    } catch (error) {
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

const SelectWinner = async (id, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/admin/participants/${id}/select-winner`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true)
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al seleccionar ganador.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

const ClearWinner = async (id, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/admin/participants/${id}/clear-winner`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true)
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al deseleccionar ganador.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

const GetAdminStats = async (roomId = null) => {
    try {
        // En V2 el endpoint principal es metrics/summary
        const url = appendRoomId(`${API_URL}PluckingTheRooster/metrics/summary`, roomId);
        const response = await fetch(url, {
            method: 'GET',
            headers: buildHeaders(roomId, true)
        });

        if (response.ok) {
            const data = await response.json();
            return { success: true, data };
        }

        // Fallback a /admin/stats si metrics/summary fallara
        const fallbackUrl = appendRoomId(`${API_URL}PluckingTheRooster/admin/stats`, roomId);
        const fbResponse = await fetch(fallbackUrl, {
            method: 'GET',
            headers: buildHeaders(roomId, true)
        });

        if (fbResponse.ok) {
            const data = await fbResponse.json();
            return { success: true, data };
        }

        return { success: false, message: 'Error al obtener estadísticas.' };
    } catch (error) {
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

const ShowOverlay = async (top = 10, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/overlay/show?top=${top}`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true)
        });
        return { success: response.ok };
    } catch {
        return { success: false, message: 'Error de red' };
    }
};

const HideOverlay = async (roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/overlay/hide`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true)
        });
        return { success: response.ok };
    } catch {
        return { success: false, message: 'Error de red' };
    }
};

const RefreshOverlay = async (top = 10, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/overlay/refresh?top=${top}`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true)
        });
        return { success: response.ok };
    } catch {
        return { success: false, message: 'Error de red' };
    }
};

const ConfigOverlay = async (configDto, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/overlay/config`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true),
            body: JSON.stringify(configDto)
        });
        return { success: response.ok };
    } catch {
        return { success: false, message: 'Error de red' };
    }
};

const ConfigGame = async (duration, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/config-game`, roomId);
        const payload = typeof duration === 'object' ? duration : { durationSeconds: duration };
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true),
            body: JSON.stringify(payload)
        });
        return { success: response.ok };
    } catch {
        return { success: false, message: 'Error de red' };
    }
};

const ResetGame = async (roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/reset`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true)
        });
        return { success: response.ok };
    } catch {
        return { success: false, message: 'Error de red' };
    }
};

const ToggleActiveGame = async (active, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/admin/toggle-active?active=${active}`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true)
        });
        return { success: response.ok };
    } catch {
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

const UpdatePosition = async (offset, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/admin/position?offset=${offset}`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true)
        });
        if (response.ok) {
            const data = await response.json().catch(() => ({}));
            return { success: true, data };
        }
        // Fallback usando ConfigOverlay
        return await ConfigOverlay({ layoutMode: 1, positionOffset: offset }, roomId);
    } catch {
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

const UpdateBannerMode = async (mode, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/admin/banner-mode?mode=${mode}`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true)
        });
        if (response.ok) {
            const data = await response.json().catch(() => ({}));
            return { success: true, data };
        }
        return { success: true };
    } catch {
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

const UpdateOverlayVisibility = async (visible, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/admin/overlay-visibility?visible=${visible}`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true)
        });
        if (response.ok) {
            const data = await response.json().catch(() => ({}));
            return { success: true, data };
        }
        // Fallback usando ShowOverlay / HideOverlay
        if (visible) {
            return await ShowOverlay(10, roomId);
        } else {
            return await HideOverlay(roomId);
        }
    } catch {
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

const TrackMetric = async (eventName, sessionId, source, roomId = null) => {
    try {
        const url = appendRoomId(`${API_URL}PluckingTheRooster/track`, roomId);
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId, true),
            body: JSON.stringify({ eventName, sessionId, source })
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al guardar métrica.' };
        }
        const data = await response.json();
        return { success: true, data };
    } catch {
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

export {
    SubmitGame,
    ValidatePlayer,
    GetSettings,
    GetAdminParticipants,
    SelectWinner,
    ClearWinner,
    GetAdminStats,
    ShowOverlay,
    HideOverlay,
    RefreshOverlay,
    ConfigOverlay,
    ConfigGame,
    ResetGame,
    ToggleActiveGame,
    UpdatePosition,
    UpdateBannerMode,
    UpdateOverlayVisibility,
    TrackMetric
};